/**
 * 共享基础类型定义
 *
 * 这个文件包含所有版本共享的基础类型定义
 */

// ==================== 基础实体类型 ====================

/**
 * 对话类型
 */
export interface Conversation {
  id: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  userId?: string;
  messageCount?: number;
}

/**
 * 消息类型
 */
export interface Message {
  id: string;
  conversationId?: string;
  role: 'user' | 'assistant' | 'system';
  content: string | MessageContent[];
  mediaIds?: string[];
  timestamp: string;
  parentMessageId?: string;
  status?: 'completed' | 'error' | 'pending' | 'streaming';
  metadata?: Record<string, any>;
  imageUrl?: string | null;
  createdAt?: string;
  tempKey?: string;
  contentUpdateTime?: string;

  // 将processLog提升为顶级字段，与message平齐
  processStep?: ProcessStep[];
}


export interface TextBlock {
  text: string;
  type: 'text';
}

export interface ImageBlock {
  source: {
    type: `url`,
    url: string
  };
  type: 'image';
}

export interface PlainTextSource {
  data: string;

  media_type: 'text/plain';

  type: 'text';
}

export interface URLPDFSource {
  type: 'url';

  url: string;
}

export interface DocumentBlock {
  source: PlainTextSource | URLPDFSource;
  type: 'document';
}



export interface ToolUseBlock {
  id: string;
  input: unknown;
  name: string;
  type: 'tool_use';
}

export type MessageContent =
  | TextBlock
  | ImageBlock
  | DocumentBlock
  | ToolUseBlock


/**
 * 项目类型
 */
export interface Project {
  id: string;
  title: string;
  description?: string | null;
  context?: string | null; // 兼容某些API
  createdAt: string;
  updatedAt: string;
  userId: string;
  color?: string | null;
  isArchived?: boolean;
  shareId?: string | null;
  isPublic?: boolean;
  ogImageUrl?: string | null;
}

/**
 * 任务类型
 */
export interface Task {
  id: string;
  title: string;
  description?: string | null;
  notes?: string | null; // 兼容某些API
  createdAt: string;
  updatedAt: string;
  userId: string;
  projectId: string | null;
  isCompleted?: boolean;
  done?: boolean; // 兼容某些API
  startAt?: string | null;
  dueAt?: string | null;
  cost?: number | null;
  vital?: boolean;
  urgent?: boolean;
  fixed?: boolean;
  tags?: string[] | Tag[];
  reminders?: Reminder[] | null;
  embedding?: number[] | null;
  doneAt?: string | null;
  project?: {
    id: string;
    title: string;
  } | null;
}

/**
 * 用户类型
 */
export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl?: string | null;
  avatar?: string | null; // 兼容某些API
  createdAt: string;
  updatedAt: string;
}

// ==================== 标签相关类型 ====================

/**
 * 标签类型
 */
export interface Tag {
  id: string;
  userId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 创建标签数据
 */
export interface CreateTagData {
  id?: string; // 客户端可能会提供ID，用于乐观更新
  title: string;
}

/**
 * 更新标签数据
 */
export interface UpdateTagData {
  id: string;
  title: string;
}

/**
 * 任务标签关联
 */
export interface TaskTag {
  taskTagId: string;
  tagId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== 提醒相关类型 ====================

/**
 * 提醒动作类型 (映射 VALARM ACTION)
 */
export type ReminderAction = 'DISPLAY' | 'AUDIO' | 'EMAIL';

/**
 * 提醒触发器关联点 (映射 VALARM TRIGGER RELATED - 扩展以区分 END 和 DUE)
 */
export enum TriggerRelation {
  START = 'START',
  // END = 'END', // 不再直接使用 END，用下面两个代替
  CALCULATED_END = 'CALCULATED_END', // 基于 startAt + cost
  DUE = 'DUE'                   // 基于 dueAt
}

/**
 * 提醒接口，基于 VALARM 核心概念
 */
export interface Reminder {
  // VALARM ACTION (映射)
  action: ReminderAction;
  // VALARM TRIGGER 核心属性
  trigger: {
    // 触发类型：相对或绝对
    type: 'RELATIVE' | 'ABSOLUTE';
    // 相对触发相关
    related?: TriggerRelation; // 关联到 START 或 END/DUE
    offsetSeconds?: number;    // 相对偏移秒数 (正数表示之后, 负数表示之前)
    // 绝对触发相关
    absoluteTime?: string;     // ISO 8601 UTC 时间字符串 (e.g., "20250420T090000Z")
  };
  description?: string; // 提醒文本 (映射 VALARM DESCRIPTION/SUMMARY)
  // 提醒方式 (映射，可以根据 action 进一步细化)
  methods: ('短信' | '微信' | '邮件' | '桌面通知')[];
}

// ==================== 任务/项目操作类型 ====================

/**
 * 创建任务数据
 */
export interface CreateTaskData {
  title: string;
  notes?: string;
  done?: boolean;
  vital?: boolean;
  urgent?: boolean;
  fixed?: boolean;
  dueAt?: string | null;
  startAt?: string | null;
  endAt?: string | null; // 虚拟字段，不直接存储但在UI/计算中有用
  cost?: number | null;
  reminders?: Reminder[] | null;
  projectId?: string | null;
  tags?: string[] | null;
}

/**
 * 更新任务数据
 */
export interface UpdateTaskData {
  id: string;
  title?: string;
  notes?: string;
  done?: boolean;
  vital?: boolean;
  urgent?: boolean;
  fixed?: boolean;
  dueAt?: string | null;
  startAt?: string | null;
  endAt?: string | null; // 虚拟字段
  cost?: number | null;
  reminders?: Reminder[] | null;
  projectId?: string | null;
  tags?: string[] | null;
}

/**
 * 创建项目数据
 */
export interface CreateProjectData {
  title: string;
  context?: string;
  description?: string;
}

/**
 * 更新项目数据
 */
export interface UpdateProjectData {
  id: string;
  title?: string;
  context?: string;
  description?: string;
}

// ==================== 搜索参数类型 ====================

/**
 * 任务搜索参数
 */
export interface TaskSearchParams {
  keywords: string[];
}

/**
 * 项目搜索参数
 */
export interface ProjectSearchParams {
  keywords: string[];
}

// ==================== 工具类型 ====================

/**
 * 时间段类型
 */
export interface Duration {
  days: number;
  hours: number;
  minutes: number;
}

/**
 * 将秒数转换为时间段对象
 */
export const secondsToDuration = (seconds: number): Duration => {
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return { days, hours, minutes };
};

/**
 * 将时间段对象转换为秒数
 */
export const durationToSeconds = (duration: Duration): number => {
  return (
    duration.days * 24 * 3600 +
    duration.hours * 3600 +
    duration.minutes * 60
  );
};


// ProcessStep类型定义，与后端数据库存储完全对齐
export type ProcessStep =
  | {
    type: 'text'; // 对应后端数据库的 'text' 类型
    text: string;
    sequence: number; // 对应后端数据库的 sequence 字段
    timestamp: string;
  }
  | {
    type: 'tool_result'; // 对应后端数据库的 'tool_result' 类型
    toolId: string;
    toolName: string;
    status: 'success' | 'error';
    result: any;
    sequence: number;
    timestamp: string;
  }
  | {
    type: 'tool_error'; // 对应后端数据库的 'tool_error' 类型
    toolId: string;
    toolName?: string; // 可选，用于区分是否为工具本身的错误
    status: 'system_error' | 'validation_error' | 'update_error' | 'fetch_project_error' | 'fetch_error';
    result?: {
      message?: string;
      details?: string;
    };
    sequence: number;
    timestamp: string;
  };

// 工具特定的类型守卫和类型定义
export interface FetchMemResult {
  totalFound: number;
  hasCompleted: number;
  hasPending: number;
  timeRange?: {
    start?: string | null;
    end?: string | null;
  } | null;
  message?: string;
  results?: any[];
}

export interface UpdateMemResult {
  taskResults?: any;
  projectResults?: any[];
}

// 类型守卫函数
export function isFetchMemStep(step: ProcessStep): step is ProcessStep & { type: 'tool_result'; toolName: 'FETCH_MEM'; result: FetchMemResult } {
  return step.type === 'tool_result' && step.toolName === 'FETCH_MEM';
}

export function isUpdateMemStep(step: ProcessStep): step is ProcessStep & { type: 'tool_result'; toolName: 'UPDATE_MEM'; result: UpdateMemResult } {
  return step.type === 'tool_result' && step.toolName === 'UPDATE_MEM';
}